// Add-a-place form (F41), reached from the map's right-click / long-press
// popover with ?lng=&lat= carrying the pressed coordinate. The geometry is
// fixed to that point — there is no coordinate editing here; picking a
// different spot means going back to the map.
import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link, useLocation, useSearchParams } from "wouter";
import { MapPin } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import { CREATE_PLACE, type CreatePlaceResult } from "../lib/queries";
import { useAuth } from "../lib/auth";
import { PLACE_TYPES, type PlaceType } from "../lib/format";
import { useT } from "../lib/i18n";
import { LoginGate } from "../components/LoginGate";
import { ErrorBox, Field, Select, TextInput } from "../components/ui";
import { Button } from "@/components/ui/button";
import { MiniMap } from "../map/MiniMap";
import { toFeature } from "../lib/geo";

export function NewPlacePage() {
  const [params] = useSearchParams();
  const search = params.toString();
  return (
    <LoginGate action="gateAction.addPlace" next={`/place/new${search ? `?${search}` : ""}`}>
      <NewPlaceForm />
    </LoginGate>
  );
}

function NewPlaceForm() {
  const [params] = useSearchParams();
  const { personId } = useAuth();
  const { t, labelizeT } = useT();
  const [, navigate] = useLocation();

  const lng = Number(params.get("lng"));
  const lat = Number(params.get("lat"));
  const hasCoords =
    Number.isFinite(lng) && Number.isFinite(lat) && params.has("lng") && params.has("lat") && Math.abs(lat) <= 90;

  const [name, setName] = useState("");
  const [type, setType] = useState<PlaceType>("SUMMIT");
  const [elevation, setElevation] = useState("");

  const create = useMutation({
    mutationFn: () =>
      gqlRequest<CreatePlaceResult>(CREATE_PLACE, {
        input: {
          name: name.trim(),
          type,
          elevation: elevation.trim() === "" ? null : Math.round(Number(elevation)),
          // GeoJSON object form — the scalar also takes WKT strings, but not
          // GeoJSON as a string. See the note on CREATE_PLACE in queries.ts.
          geom: { type: "Point", coordinates: [lng, lat] },
          // RLS insists this matches the JWT claim; nothing defaults it.
          createdBy: personId,
        },
      }),
    onSuccess: (data) => navigate(`/place/${data.createPlace.place.id}`),
  });

  if (!hasCoords) {
    return (
      <div className="mx-auto max-w-md p-6 text-center text-sm text-muted-foreground">
        <MapPin aria-hidden className="mx-auto mb-2 size-6 text-muted-foreground" />
        {t("newPlace.pickPre")}{" "}
        <Link href="/" className="font-semibold text-primary hover:underline">
          {t("newPlace.pickMap")}
        </Link>{" "}
        {t("newPlace.pickPost")}
      </div>
    );
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (name.trim() && !create.isPending) create.mutate();
  }

  return (
    <div className="mx-auto max-w-xl space-y-4 p-4 sm:p-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">{t("newPlace.heading")}</p>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t("newPlace.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {lat.toFixed(5)}, {lng.toFixed(5)}
        </p>
      </div>

      <MiniMap
        features={[toFeature({ type: "Point", coordinates: [lng, lat] }, { id: "new-place" })]}
        className="h-56 w-full overflow-hidden rounded-xl border shadow-card"
      />

      <form onSubmit={onSubmit} className="space-y-4">
        <Field label={t("common.name")}>
          <TextInput required autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={t("newPlace.namePlaceholder")} />
        </Field>
        <Field label={t("common.type")}>
          <Select value={type} onChange={(e) => setType(e.target.value as PlaceType)}>
            {PLACE_TYPES.map((pt) => (
              <option key={pt} value={pt}>
                {labelizeT(pt)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("place.elevationOptional")}>
          <TextInput
            type="number"
            inputMode="numeric"
            min={-500}
            max={9000}
            value={elevation}
            onChange={(e) => setElevation(e.target.value)}
            placeholder="e.g. 1603"
          />
        </Field>
        {create.error != null && <ErrorBox error={create.error} />}
        <Button type="submit" disabled={create.isPending || !name.trim()}>
          {create.isPending ? t("newPlace.busy") : t("newPlace.submit")}
        </Button>
      </form>
    </div>
  );
}
